// src/registered/site-admin/report_component_library/ReportComponentLibraryController.js
//
// Group-ESG-persona CRUD over the reusable Report Component Library. Owns all
// state and returns a flat props object for the view.

import { useCallback, useEffect, useMemo, useState } from "react";
import ReportComponentDTO, {
  ReportComponentDisclosureDTO,
  KIND_TO_RENDER_TYPE,
} from "./ReportComponentDTO";
import {
  listComponents,
  getComponent,
  saveComponent,
  deactivateComponent,
} from "./reportComponentLibraryService";
import { GoldenSourceHelper } from "../../../app/Switch_Core_React_Standards";
import {
  fetchTaxonomyHeaders,
  fetchTaxonomyDetailsByHeader,
} from "../components/Workflow-esg/services/EsgTaxonomySyncService";

/**
 * Coarse "what shape is this datapoint" bucket, used as a secondary facet under
 * the disclosure search box. Falls back to the label's trailing [..] tag when
 * the taxonomy row carries no data type.
 */
function classifyKind(label, dataType) {
  const l = (label || "").toLowerCase();
  const t = (dataType || "").toLowerCase();
  if (l.endsWith("[text block]") || /textblock/.test(t)) return "Narrative (text block)";
  if (/percent/.test(t) || /\bpercentage\b/.test(l)) return "Percentage";
  if (/monetary/.test(t)) return "Monetary";
  if (/(decimal|integer|shares|pure|area|volume|mass|energy|power|pershare|gyear|duration)/.test(t))
    return "Numeric";
  if (/bool/.test(t)) return "Yes / No";
  if (/date/.test(t)) return "Date";
  if (/(enum|gyearlist)/.test(t)) return "Choice (enumeration)";
  if (/(string|text|uri|anyuri)/.test(t)) return "Text";
  if (t) return "Other";
  return "Value";
}

export default function ReportComponentLibraryController() {
  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const [draft, setDraft] = useState(null); // ReportComponentDTO | null
  const [isNew, setIsNew] = useState(false);
  const [baseline, setBaseline] = useState(null); // pristine copy for version bump
  const [disclosureFilter, setDisclosureFilter] = useState("");

  // Taxonomy concepts a component's disclosures are bound to (latest synced set)
  const siteKey = process.env.REACT_APP_SITE_KEY;
  const [concepts, setConcepts] = useState([]); // {rowId, xbrlTag, measureName, headerId}
  const [conceptsLoading, setConceptsLoading] = useState(false);
  const [conceptsNote, setConceptsNote] = useState("");

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { items, remoteOk, remoteError } = await listComponents();
      setComponents(items);
      if (!remoteOk && remoteError) {
        setNotice(`Cloud store unavailable — showing local drafts. ${remoteError}`);
      } else {
        setNotice("");
      }
    } catch (err) {
      setError(err?.message || "Could not load the component library.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Load the concept list for the disclosure picker — latest synced taxonomy.
  useEffect(() => {
    let alive = true;
    (async () => {
      setConceptsLoading(true);
      try {
        const headers = await fetchTaxonomyHeaders({ siteKey });
        const sorted = [...(headers || [])].sort((a, b) => {
          const ay = Number(GoldenSourceHelper.get(a, "TaxYear")) || 0;
          const by = Number(GoldenSourceHelper.get(b, "TaxYear")) || 0;
          if (by !== ay) return by - ay;
          return String(GoldenSourceHelper.get(b, "VersionLabel") || "").localeCompare(
            String(GoldenSourceHelper.get(a, "VersionLabel") || ""),
          );
        });
        const headerId = sorted[0]
          ? GoldenSourceHelper.get(sorted[0], "HeaderId")
          : null;
        if (!headerId) {
          if (alive) {
            setConcepts([]);
            setConceptsNote(
              "No ingested taxonomy found — ingest one in the XBRL Processor, or type a disclosure name manually.",
            );
          }
          return;
        }
        const rows = await fetchTaxonomyDetailsByHeader(headerId, { siteKey });

        // The detail set is the FULL concept catalogue — line items plus all the
        // structure (dimension axes, their members, tables, abstract headings).
        // Only the line items are things a component "reports"; hide the rest.
        const STRUCTURAL =
          /\s*\[(member|axis|abstract|table|line items|roll ?up|hypercube|domain|heading|title|dimension)\]\s*$/i;

        const all = (rows || []).map((r) => {
          const measureName = GoldenSourceHelper.get(r, "MeasureName") || "";
          const xbrlTag = GoldenSourceHelper.get(r, "XbrlTag") || "";
          const dataType =
            GoldenSourceHelper.get(r, "DataType") ||
            GoldenSourceHelper.get(r, "ItemType") ||
            "";
          const localName = xbrlTag.includes("}")
            ? xbrlTag.slice(xbrlTag.indexOf("}") + 1)
            : xbrlTag;
          const cleanLabel = measureName.replace(STRUCTURAL, "").trim() || localName;
          return {
            rowId: String(GoldenSourceHelper.get(r, "RowId")),
            xbrlTag,
            localName,
            measureName,
            cleanLabel,
            dataType,
            kind: classifyKind(cleanLabel, dataType),
            structural: STRUCTURAL.test(measureName),
            headerId: String(headerId),
          };
        });

        const mapped = all
          .filter((r) => !r.structural && (r.cleanLabel || r.localName))
          .sort((a, b) => a.cleanLabel.localeCompare(b.cleanLabel));

        if (alive) {
          setConcepts(mapped);
          setConceptsNote(
            mapped.length
              ? ""
              : "No reportable datapoints in the synced taxonomy — type a disclosure name manually.",
          );
        }
      } catch {
        if (alive) {
          setConcepts([]);
          setConceptsNote(
            "Could not load taxonomy concepts — type a disclosure name manually.",
          );
        }
      } finally {
        if (alive) setConceptsLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [siteKey]);

  const disclosures = useMemo(
    () =>
      [...new Set(components.map((c) => c.primaryDisclosure).filter(Boolean))].sort(),
    [components],
  );

  const visibleComponents = useMemo(
    () =>
      disclosureFilter
        ? components.filter((c) => c.primaryDisclosure === disclosureFilter)
        : components,
    [components, disclosureFilter],
  );

  // --- editing ------------------------------------------------------------

  const newComponent = () => {
    setDraft(new ReportComponentDTO());
    setBaseline(null);
    setIsNew(true);
    setError("");
  };

  const editComponent = async (id) => {
    setError("");
    const full = await getComponent(id);
    if (!full) {
      setError(`Could not open component ${id}.`);
      return;
    }
    setDraft(full);
    setBaseline(JSON.parse(JSON.stringify(full)));
    setIsNew(false);
  };

  const cancelEdit = () => {
    setDraft(null);
    setBaseline(null);
    setIsNew(false);
  };

  const patchDraft = (mutator) =>
    setDraft((prev) => {
      if (!prev) return prev;
      const next = new ReportComponentDTO(JSON.parse(JSON.stringify(prev)));
      mutator(next);
      return next;
    });

  const setField = (field, value) => patchDraft((d) => { d[field] = value; });

  const setBindingField = (field, value) =>
    patchDraft((d) => { d.bindingCapability[field] = value; });

  // --- disclosures (what this component reports) --------------------------

  const taxRef = (d) => d.rowId || `free:${d.measureName}`;

  const addDisclosure = (concept) =>
    patchDraft((d) => {
      const name = concept?.cleanLabel || concept?.measureName || "";
      if (!concept || (!name && !concept.xbrlTag)) return;
      const ref = taxRef({ ...concept, measureName: name });
      if (d.boundDisclosures.some((x) => taxRef(x) === ref)) return; // no dup datapoint
      d.boundDisclosures.push(
        new ReportComponentDisclosureDTO({
          rowId: concept.rowId || null,
          xbrlTag: concept.xbrlTag || "",
          measureName: name,
          headerId: concept.headerId || null,
          // each disclosure carries its own presentation block
          label: name,
          renderType: KIND_TO_RENDER_TYPE[concept.kind] || "",
          guidance: "",
        }),
      );
    });

  const removeDisclosure = (id) =>
    patchDraft((d) => {
      d.boundDisclosures = d.boundDisclosures.filter((x) => x.id !== id);
    });

  const setDisclosureField = (id, field, value) =>
    patchDraft((d) => {
      const disc = d.boundDisclosures.find((x) => x.id === id);
      if (disc) disc[field] = value;
    });

  // --- validation + save --------------------------------------------------

  const validationErrors = useMemo(() => {
    if (!draft) return [];
    const errs = [];
    const discs = draft.boundDisclosures || [];
    if (discs.length === 0) {
      errs.push("Add at least one disclosure this component reports.");
    }
    discs.forEach((disc, i) => {
      const tag = `Disclosure ${i + 1} (${disc.measureName || disc.xbrlTag})`;
      if (!(disc.label || "").trim()) errs.push(`${tag}: a label is required.`);
      if (!disc.renderType) errs.push(`${tag}: choose how it presents.`);
    });
    return errs;
  }, [draft]);

  const canSave = draft && validationErrors.length === 0 && !saving;

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const { component, persistedRemote, remoteError } = await saveComponent(
        draft,
        { existing: baseline },
      );
      setDraft(component);
      setBaseline(JSON.parse(JSON.stringify(component)));
      setIsNew(false);
      await refresh();
      setNotice(
        persistedRemote
          ? `Saved. "${component.label}" is now v${component.version}.`
          : `Saved locally. Cloud sync failed: ${remoteError}`,
      );
    } catch (err) {
      setError(err?.message || "Could not save the component.");
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async (id) => {
    setError("");
    try {
      await deactivateComponent(id);
      if (draft?.id === id) cancelEdit();
      await refresh();
      setNotice("Component retired.");
    } catch (err) {
      setError(err?.message || "Could not retire the component.");
    }
  };

  return {
    // list
    components: visibleComponents,
    allCount: components.length,
    disclosures,
    disclosureFilter,
    setDisclosureFilter,
    loading,
    // editor
    draft,
    isNew,
    saving,
    canSave,
    validationErrors,
    error,
    notice,
    // disclosure picker
    concepts,
    conceptsLoading,
    conceptsNote,
    addDisclosure,
    removeDisclosure,
    setDisclosureField,
    // actions
    refresh,
    newComponent,
    editComponent,
    cancelEdit,
    setField,
    setBindingField,
    save,
    deactivate,
  };
}
